/**
 * Atlas PM · 任务详情（设计稿 12）· 左列：指标与要素
 *
 * ⭐ P6.41 双链路：
 *   - 完成度：要素完成度 → 指标完成度 → 任务完成度
 *   - 质量：  要素质量   → 指标质量   → 任务质量（平行，互不影响）
 *   要素的完成度/质量/附件统一由 `ElementProgressControl` 编辑，
 *   写库后后端在同事务内重算，前端条自动刷新。
 */

import Badge from '../../components/Badge';
import EmptyState from '../../components/EmptyState';
import ElementProgressControl from './ElementProgressControl';
import type { MetricInfo, TaskProgressInfo } from '@atlaspm/shared';
import type { EditPolicy } from './useTaskDetail';

export interface DetailMetricColumnProps {
  taskId: string;
  metrics: MetricInfo[];
  progress?: TaskProgressInfo;
  policy: EditPolicy;
}

function metricTypeLabel(metricType: string): string {
  const t = (metricType || '').toUpperCase();
  if (t === 'RATE' || t === 'PERCENT' || t === 'PERCENTAGE') return '百分比';
  if (t === 'BOOL' || t === 'BOOLEAN') return '布尔达标';
  if (t === 'COUNT') return '数值';
  if (t === 'SCORE') return '评分';
  return metricType || '指标';
}

function elementTypeLabel(elType: string): string {
  switch ((elType || '').toUpperCase()) {
    case 'BOOL':
      return '布尔';
    case 'NUMBER':
      return '数值';
    case 'TEXT':
      return '文本';
    case 'DATE':
      return '日期';
    case 'ENUM':
      return '枚举';
    case 'FILE':
      return '文件';
    default:
      return elType || '要素';
  }
}

function clamp(n: number): number {
  return Math.max(0, Math.min(100, n));
}

export default function DetailMetricColumn({
  taskId,
  metrics,
  progress,
  policy,
}: DetailMetricColumnProps) {
  const weightSum = metrics.reduce((s, m) => s + (m.weight ?? 0), 0);
  const progressPct = progress?.progressPct ?? 0;
  const qualityPct = progress?.qualityPct ?? 0;

  return (
    <section className="dt-card dcol" data-col="metrics" aria-label="指标与要素">
      <header className="dt-card__head">
        <h3 className="dt-card__title">指标与要素 · {metrics.length} 个指标</h3>
        <span className="dt-card__extra">权重合计 {weightSum.toFixed(0)} / 100</span>
      </header>

      <div className="dt-card__body">
        {metrics.length > 0 && (
          <div className="dt-formula">
            <div>
              完成度 = Σ(指标权重 × 指标完成度) / 100 ={' '}
              <b>{progressPct.toFixed(1)}%</b>
            </div>
            <div>
              质量 = Σ(指标权重 × 指标质量) / 100 = <b>{qualityPct.toFixed(1)}%</b>
            </div>
          </div>
        )}

        {metrics.length === 0 ? (
          <EmptyState icon="◇" title="暂无指标" hint="创建任务时可声明指标与要素，进度由系统计算。" />
        ) : (
          metrics.map((m) => {
            const pct = clamp(m.completenessPct);
            const quality = clamp(m.qualityPct);
            const achieved = progress?.metrics.find((x) => x.metricId === m.id)?.achieved ?? m.achieved;

            return (
              <div className="dt-metric" key={m.id}>
                <div className="dt-metric__head">
                  <span className="dt-metric__name">{m.name}</span>
                  <Badge tone="idle">{metricTypeLabel(m.metricType)}</Badge>
                  {achieved && <Badge tone="ok">达标</Badge>}
                  <span className="dt-metric__val num">完成 {m.completenessPct.toFixed(0)}%</span>
                  <span className="dt-metric__val num dt-metric__val--q">质量 {m.qualityPct.toFixed(0)}%</span>
                </div>

                <div className="dt-metric__bar">
                  <span style={{ width: `${pct}%` }} />
                </div>
                <div className="dt-metric__bar dt-metric__bar--q">
                  <span style={{ width: `${quality}%` }} />
                </div>

                <div className="dt-metric__sub">
                  <span className="dt-metric__w">权重 {m.weight}</span>
                  <span>
                    要素达标 {m.elements.filter((e) => e.completionPct >= 100).length}/{m.elements.length}
                  </span>
                </div>

                <div className="dt-els">
                  {m.elements.map((el) => (
                    <div className="dt-el dt-el--rich" key={el.id} data-el-id={el.id}>
                      <div className="dt-el__main">
                        <div className="dt-el__name">
                          {el.name}
                          {el.isRequired && <em className="dt-el__req">必填</em>}
                          <span className="dt-el__type">{elementTypeLabel(el.elementType)}</span>
                        </div>
                        {el.description && <div className="dt-el__desc">{el.description}</div>}
                      </div>

                      <ElementProgressControl
                        taskId={taskId}
                        element={el}
                        canEdit={policy.canEditElements}
                      />

                      <span className="dt-el__w">权重 {el.weight}%</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
